import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@nextui-org/react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  loadCaptchaEnginge,
  LoadCanvasTemplateNoReload,
  validateCaptcha,
} from "react-simple-captcha";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSubmitContactForm } from "@/services/profile.service";

const phoneRegex = new RegExp(/^[+](\d{5,24})$/);

const contactFormSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required.")
    .max(32, "Input too long"),
  lastName: z
    .string()
    .min(1, "Last name is required.")
    .max(32, "Input too long"),
  email: z
    .string()
    .min(1, "Email is required.")
    .email({ message: "Please enter a valid email address." }),
  phone: z
    .string()
    .min(1, "Phone is required.")
    .regex(phoneRegex, "Phone number format started with country code"),
  message: z.string().min(1, "Message is required."),
});

export function ContactFormPage() {
  const { handleSubmit, getValues, register, formState } = useForm({
    resolver: zodResolver(contactFormSchema),
  });

  const { mutate, isSuccess, isError } = useSubmitContactForm();

  const handleFormSubmit = async () => {
    const { captcha } = getValues();

    if (!validateCaptcha(captcha)) {
      toast("Invalid captcha", {
        type: "error",
      });
      return;
    }

    mutate();
  };

  useEffect(() => {
    loadCaptchaEnginge(6, "white", "black", "numbers");
  }, []);

  useEffect(() => {
    if (isSuccess) {
      toast("Your form has been submitted. Thank you!", {
        type: "success",
      });
      setTimeout(() => {
        window.location.replace("/");
      }, 3000);
    }

    if (isError) {
      toast("failed sending support mail", {
        type: "error",
      });
    }
  }, [isSuccess, isError]);

  return (
    <div className="p-4 text-white lg:px-12 lg:py-8">
      <h1 className="mb-4 text-[30px] font-[400]">Get in Touch</h1>
      <p className="mb-4 text-[16px] font-thin leading-[18px]">
        Send your enquiries through the form on the below. Our team will respond
        immediately
      </p>
      <div>
        <form
          className="flex flex-col gap-3 lg:w-full"
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          <div className="flex flex-col gap-3 md:flex-row">
            <div className="flex w-full flex-col gap-2">
              <label
                htmlFor="firstName"
                className="text-sm font-normal text-white"
              >
                First Name
              </label>
              <Input
                {...register("firstName")}
                placeholder="e.g., John"
                name="firstName"
                className={`w-full !cursor-text rounded-sm bg-input p-1`}
              />
              <p className="text-xs text-danger">
                {formState.errors?.firstName?.message as string}
              </p>
            </div>
            <div className="flex w-full flex-col gap-2">
              <label
                htmlFor="lastName"
                className="text-sm font-normal text-white"
              >
                Last Name
              </label>
              <Input
                {...register("lastName")}
                placeholder="e.g., Doe"
                name="lastName"
                className={`w-full !cursor-text rounded-sm bg-input p-1`}
              />
              <p className="text-xs text-danger">
                {formState.errors?.lastName?.message as string}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="flex w-full flex-col gap-2">
              <label htmlFor="email" className="text-sm font-normal text-white">
                Email Address
              </label>
              <Input
                {...register("email")}
                placeholder="e.g., johndoe@supermail.com"
                name="email"
                className={`w-full !cursor-text rounded-sm bg-input p-1`}
              />
              <p className="text-xs text-danger">
                {formState.errors?.email?.message as string}
              </p>
            </div>
            <div className="flex w-full flex-col gap-2">
              <label htmlFor="phone" className="text-sm font-normal text-white">
                Phone Number
              </label>
              <Input
                {...register("phone")}
                placeholder="+65 577876"
                name="phone"
                className={`w-full !cursor-text rounded-sm bg-input p-1`}
              />
              <p className="text-xs text-danger">
                {formState.errors?.phone?.message as string}
              </p>
            </div>
          </div>
          <div>
            <div className="flex flex-col gap-2">
              <label
                htmlFor="message"
                className="text-sm font-normal text-white"
              >
                Message
              </label>
              <Textarea
                {...register("message")}
                placeholder="e.g., How to increase my chance to win"
                name="message"
                minRows={4}
                maxRows={12}
                className={`min-h-[10rem] w-full !cursor-text rounded-sm bg-input p-1`}
              />

              <p className="text-xs text-danger">
                {formState.errors?.message?.message as string}
              </p>
            </div>
            <div className="flex flex-col gap-4 md:flex-row md:items-end">
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="captcha"
                  className="text-sm font-normal text-white"
                >
                  CAPTCHA
                </label>
                <Input
                  {...register("captcha")}
                  placeholder="CAPTCHA"
                  name="captcha"
                  className={`w-full !cursor-text rounded-sm bg-input p-1`}
                />
              </div>
              <LoadCanvasTemplateNoReload />
            </div>
          </div>
          <Button
            className={`mt-4 flex rounded-sm bg-button-gradient text-white md:self-end md:text-lg lg:w-[150px]`}
          >
            Send
          </Button>
        </form>
      </div>
    </div>
  );
}
